/**
 * BL-2279 (PRJ-89) — `deriveCloseStage`: a closed auction's post-close
 * settlement stage, derived from its stage stamps, never stored (ADR 023 — Predicate Lifecycle).
 */

import type { AuctionPhaseKind } from './phase-tokens';

/**
 * The PLATFORM maximum of post-close settlement stages: the four stamp columns,
 * the request schema's `max`, and the most labels a house may name. It is not
 * the house's own count. The guard bounds `set-close-stage` by the stages the
 * house names (`configuredCloseStageCount`, BL-2281), which may be fewer.
 */
export const AUCTION_CLOSE_STAGE_COUNT = 4;

/** A post-close settlement stage: 0 (closed, no stage reached) through 4. */
export type CloseStage = 0 | 1 | 2 | 3 | 4;

/** A named closing-stage position, 1 through 4 (BL-2281): the stage a house labels, never 0. */
export type CloseStagePosition = Exclude<CloseStage, 0>;

/**
 * Narrow a number to a named closing-stage position (BL-2281).
 *
 * @param n - A candidate stage, e.g. a house's stored stage count.
 * @returns True when `n` is an integer from 1 through {@link AUCTION_CLOSE_STAGE_COUNT}.
 */
export function isCloseStagePosition(n: number): n is CloseStagePosition {
  return Number.isInteger(n) && n >= 1 && n <= AUCTION_CLOSE_STAGE_COUNT;
}

/** The auction stamps the derivation reads: `closed_at` plus the four stage stamps. */
export interface CloseStageStamps {
  closedAt: Date | string | null;
  closeStage1At: Date | string | null;
  closeStage2At: Date | string | null;
  closeStage3At: Date | string | null;
  closeStage4At: Date | string | null;
}

/**
 * Derive an auction's post-close settlement stage.
 *
 * An auction that is not closed is at stage 0 whatever its stage stamps hold —
 * the phase gates the stage, so a stale stamp can never surface on an open
 * auction. A closed auction is at the highest stage reached contiguously from
 * stage 1: a gap stops the count, mirroring `auctions_close_stage_order_chk`.
 *
 * @param stamps - the auction's `closedAt` and `closeStage1At`…`closeStage4At`.
 * @returns the stage, 0 through {@link AUCTION_CLOSE_STAGE_COUNT}.
 */
export function deriveCloseStage(stamps: CloseStageStamps): CloseStage {
  if (stamps.closedAt == null) return 0;
  const ordered = [stamps.closeStage1At, stamps.closeStage2At, stamps.closeStage3At, stamps.closeStage4At];
  let stage = 0;
  for (const stamp of ordered) {
    if (stamp == null) break;
    stage += 1;
  }
  return stage as CloseStage;
}

/**
 * Derive the stage an auction DISPLAYS, gated on its already-derived phase.
 *
 * A soft-deleted auction keeps its `closedAt` and stage stamps, so
 * {@link deriveCloseStage} alone would still report a stage for it; the
 * display surfaces (the /auctions list and the auction chrome) carry a stage
 * only while the derived phase is `closed`.
 *
 * @param opts.phase - The auction's derived phase kind.
 * @param opts.stamps - The auction's `closedAt` and `closeStage1At`…`closeStage4At`.
 * @returns The derived stage when `phase` is `closed`, otherwise 0.
 */
export function deriveCloseStageForPhase(opts: { phase: AuctionPhaseKind; stamps: CloseStageStamps }): CloseStage {
  return opts.phase === 'closed' ? deriveCloseStage(opts.stamps) : 0;
}
